import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Paper, 
  Tabs, 
  Tab, 
  Divider,
  CircularProgress,
  Tooltip,
  IconButton,
  Fade,
  Chip,
  Alert,
  Button,
  LinearProgress,
  Modal,
  Backdrop,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Slider,
  Popover,
  TextField
} from '@mui/material';
import NavBar from '../components/NavBar';
import { 
  Play, 
  ExternalLink, 
  Eye,
  MousePointer,
  Keyboard,
  Video,
  FileSpreadsheet,
  Clock,
  CheckCircle, 
  XCircle,
  Layers,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  X,
  AlertTriangle,
  ChevronDown,
  Info,
  CheckCircle2,
  AlertCircle,
  Pause,
  Volume2,
  VolumeX,
  Maximize2,
  SkipForward,
  MessageCircle
} from 'lucide-react';
import { useSidebar } from '../contexts/SidebarContext';
import { formatDistanceToNow } from 'date-fns';
import axios from 'axios';
import { supabase } from '../supabaseClient';
import { API_URL } from '../globals';
import JSZip from 'jszip';

// Add this utility function at the top of the file
const cleanAnsiCodes = (text) => {
  if (!text) return '';
  // Remove ANSI color codes and other formatting
  return text.replace(/\u001b\[\d+m/g, '')
            .replace(/\u001b\[2m/g, '')
            .replace(/\u001b\[22m/g, '');
};

// Custom Video Player Component with Steps Sidebar
const CustomVideoPlayer = ({ videoUrl, steps, posterUrl }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [activeStepIndex, setActiveStepIndex] = useState(-1);
  const [showControls, setShowControls] = useState(true);
  const controlsTimeoutRef = useRef(null);

  // Convert video timestamps from milliseconds to seconds
  const stepsWithSeconds = steps?.steps?.map(step => ({
    ...step,
    videoTimestampSeconds: step.videoTimestamp / 1000
  })) || [];

  // Get total video duration in seconds (from steps or video element)
  const totalDurationSeconds = duration || (stepsWithSeconds.length > 0 ? 
    Math.max(...stepsWithSeconds.map(s => s.videoTimestampSeconds)) + 5 : 0);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
      
      // Find the active step based on current time
      const currentStepIndex = stepsWithSeconds.findIndex((step, index) => {
        const nextStep = stepsWithSeconds[index + 1];
        return video.currentTime >= step.videoTimestampSeconds && 
               (!nextStep || video.currentTime < nextStep.videoTimestampSeconds);
      });
      setActiveStepIndex(currentStepIndex);
    };

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
    };
  }, [stepsWithSeconds]);

  // Handle mouse movement to show/hide controls
  const handleMouseMove = () => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = setTimeout(() => {
      if (isPlaying) {
        setShowControls(false);
      }
    }, 3000);
  };

  const togglePlayPause = () => {
    const video = videoRef.current;
    if (video.paused) {
      video.play();
    } else {
      video.pause();
    }
  };

  const handleSeek = (newTime) => {
    const video = videoRef.current;
    video.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const jumpToStep = (stepIndex) => {
    const step = stepsWithSeconds[stepIndex];
    if (step) {
      handleSeek(step.videoTimestampSeconds);
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const handleVolumeChange = (newVolume) => {
    const video = videoRef.current;
    video.volume = newVolume;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getProgressPercentage = () => {
    return totalDurationSeconds > 0 ? (currentTime / totalDurationSeconds) * 100 : 0;
  };

  const getStepPosition = (stepTimestamp) => {
    return totalDurationSeconds > 0 ? (stepTimestamp / totalDurationSeconds) * 100 : 0;
  };

  return (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: '#1A1A1A',
        borderRadius: 1.5,
        border: '1px solid rgba(255, 255, 255, 0.05)',
        overflow: 'hidden',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        '@keyframes pulse': {
          '0%': {
            opacity: 1,
          },
          '50%': {
            opacity: 0.6,
          },
          '100%': {
            opacity: 1,
          },
        }
      }}
    >
      {/* Main Container with Sidebar Layout */}
      <Box sx={{ display: 'flex', height: 'auto' }}>
        
        {/* Video Player Section */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Video Container */}
          <Box 
            sx={{ 
              position: 'relative',
              width: '100%',
              backgroundColor: '#000',
              cursor: showControls ? 'default' : 'none'
            }}
            onMouseMove={handleMouseMove}
            onMouseLeave={() => {
              if (isPlaying) {
                setShowControls(false);
              }
            }}
          >
            <video
              ref={videoRef}
              width="100%"
              poster={posterUrl}
              onClick={togglePlayPause}
              style={{
                display: 'block',
                backgroundColor: '#000'
              }}
            >
              <source src={videoUrl} type="video/webm" />
              Your browser does not support the video tag.
            </video>

            {/* Video Controls Overlay */}
            <Fade in={showControls} timeout={300}>
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  background: 'linear-gradient(transparent, rgba(0, 0, 0, 0.9))',
                  p: 3,
                  pt: 6
                }}
              >
                {/* Progress Bar with Step Markers */}
                <Box sx={{ mb: 3, position: 'relative' }}>
                  {/* Main Progress Bar */}
                  <Box
                    sx={{
                      height: 8,
                      backgroundColor: 'rgba(255, 255, 255, 0.15)',
                      borderRadius: 4,
                      position: 'relative',
                      cursor: 'pointer',
                      boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.3)'
                    }}
                    onClick={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const clickPosition = (e.clientX - rect.left) / rect.width;
                      const newTime = clickPosition * totalDurationSeconds;
                      handleSeek(newTime);
                    }}
                  >
                    {/* Progress Fill */}
                    <Box
                      sx={{
                        height: '100%',
                        backgroundColor: '#1976d2',
                        borderRadius: 4,
                        width: `${getProgressPercentage()}%`,
                        transition: 'width 0.1s ease',
                        boxShadow: '0 0 8px rgba(25, 118, 210, 0.5)'
                      }}
                    />

                    {/* Step Markers */}
                    {stepsWithSeconds.map((step, index) => (
                      <Tooltip
                        key={index}
                        title={
                          <Box sx={{ p: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                              Step {index + 1} • {step.videoTimestampFormatted}
                            </Typography>
                            <Typography variant="caption" sx={{ lineHeight: 1.4 }}>
                              {step.description.length > 100 ? 
                                `${step.description.substring(0, 100)}...` : 
                                step.description}
                            </Typography>
                          </Box>
                        }
                        placement="top"
                        arrow
                      >
                        <Box
                          sx={{
                            position: 'absolute',
                            top: -4,
                            left: `${getStepPosition(step.videoTimestampSeconds)}%`,
                            transform: 'translateX(-50%)',
                            width: 16,
                            height: 16,
                            borderRadius: '50%',
                                                         backgroundColor: index === activeStepIndex ? '#1976d2' : 'rgba(150, 150, 150, 0.9)',
                             border: '2px solid rgba(0, 0, 0, 0.6)',
                            cursor: 'pointer',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                         boxShadow: index === activeStepIndex ? 
                               '0 0 12px rgba(25, 118, 210, 0.8), 0 2px 8px rgba(0, 0, 0, 0.3)' : 
                               '0 2px 8px rgba(0, 0, 0, 0.3)',
                                                         '&:hover': {
                               transform: 'translateX(-50%) scale(1.3)',
                               backgroundColor: '#1976d2',
                               border: '2px solid rgba(25, 118, 210, 0.8)',
                               boxShadow: '0 0 12px rgba(25, 118, 210, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4)'
                             }
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            jumpToStep(index);
                          }}
                        />
                      </Tooltip>
                    ))}
                  </Box>
                </Box>

                {/* Control Buttons */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {/* Play/Pause Button */}
                    <IconButton
                      onClick={togglePlayPause}
                      sx={{ 
                        color: 'white', 
                        p: 1.5,
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: 2,
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          transform: 'scale(1.05)'
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {isPlaying ? <Pause size={24} /> : <Play size={24} />}
                    </IconButton>

                    {/* Volume Controls */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <IconButton
                        onClick={toggleMute}
                        sx={{ 
                          color: 'white', 
                          p: 1,
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)'
                          }
                        }}
                      >
                        {isMuted || volume === 0 ? <VolumeX size={20} /> : <Volume2 size={20} />}
                      </IconButton>
                      <Slider
                        size="small"
                        value={isMuted ? 0 : volume}
                        onChange={(_, newValue) => handleVolumeChange(newValue)}
                        min={0}
                        max={1}
                        step={0.1}
                        sx={{
                          width: 80,
                          color: '#1976d2',
                          '& .MuiSlider-thumb': {
                            width: 16,
                            height: 16,
                            backgroundColor: '#fff',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                            '&:hover': {
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)'
                            }
                          },
                          '& .MuiSlider-track': {
                            height: 4,
                            backgroundColor: '#1976d2'
                          },
                          '& .MuiSlider-rail': {
                            height: 4,
                            backgroundColor: 'rgba(255, 255, 255, 0.2)'
                          }
                        }}
                      />
                    </Box>

                    {/* Time Display */}
                    <Typography sx={{ 
                      color: 'white', 
                      fontSize: '0.9rem', 
                      minWidth: 90,
                      fontFamily: 'monospace',
                      backgroundColor: 'rgba(0, 0, 0, 0.3)',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 1
                    }}>
                      {formatTime(currentTime)} / {formatTime(totalDurationSeconds)}
                    </Typography>
                  </Box>

                  {/* Right Controls */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* Next Step Button */}
                    {activeStepIndex < stepsWithSeconds.length - 1 && (
                      <Tooltip title="Next Step" arrow>
                        <IconButton
                          onClick={() => jumpToStep(activeStepIndex + 1)}
                                                     sx={{ 
                             color: '#1976d2', 
                             p: 1,
                             backgroundColor: 'rgba(25, 118, 210, 0.1)',
                             border: '1px solid rgba(25, 118, 210, 0.3)',
                             borderRadius: 2,
                             '&:hover': {
                               backgroundColor: 'rgba(25, 118, 210, 0.2)',
                               transform: 'scale(1.05)'
                             },
                             transition: 'all 0.2s ease'
                           }}
                        >
                          <SkipForward size={20} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              </Box>
            </Fade>
          </Box>
        </Box>

        {/* Steps Sidebar */}
        {stepsWithSeconds.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              width: 400,
              backgroundColor: '#242424',
              borderLeft: '1px solid rgba(255, 255, 255, 0.05)',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '70vh',
              overflow: 'hidden'
            }}
          >
            {/* Sidebar Header */}
            <Box sx={{ 
              p: 3, 
              borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
              backgroundColor: '#2A2A2A'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Box sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(25, 118, 210, 0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Video size={16} style={{ color: '#1976d2' }} />
                </Box>
                <Typography sx={{ 
                  color: '#fff', 
                  fontWeight: 600, 
                  fontSize: '1.1rem',
                  letterSpacing: '0.02em'
                }}>
                  Test Steps
                </Typography>
              </Box>
              <Typography sx={{ color: '#aaa', fontSize: '0.875rem' }}>
                {stepsWithSeconds.length} steps • {activeStepIndex >= 0 ? `Step ${activeStepIndex + 1} active` : 'Click to navigate'}
              </Typography>
            </Box>

            {/* Steps List */}
            <Box sx={{ 
              flex: 1, 
              overflow: 'auto',
              '&::-webkit-scrollbar': {
                width: 6
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)'
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                borderRadius: 3,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.3)'
                }
              }
            }}>
              <Box sx={{ p: 2 }}>
                {stepsWithSeconds.map((step, index) => (
                  <Paper
                    key={index}
                    elevation={0}
                                         sx={{
                       backgroundColor: index === activeStepIndex ? 
                         'rgba(25, 118, 210, 0.08)' : 
                         'rgba(255, 255, 255, 0.02)',
                       border: index === activeStepIndex ? 
                         '1px solid rgba(25, 118, 210, 0.3)' : 
                         '1px solid rgba(255, 255, 255, 0.05)',
                       borderRadius: 2,
                       p: 2.5,
                       mb: 2,
                       cursor: 'pointer',
                       position: 'relative',
                       overflow: 'hidden',
                       transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                       transform: index === activeStepIndex ? 'translateX(4px)' : 'translateX(0)',
                                                '&:hover': {
                           backgroundColor: index === activeStepIndex ? 
                             'rgba(25, 118, 210, 0.12)' : 
                             'rgba(255, 255, 255, 0.08)',
                           borderColor: index === activeStepIndex ? 
                             'rgba(25, 118, 210, 0.5)' : 
                             'rgba(255, 255, 255, 0.15)',
                           transform: 'translateX(6px)',
                           boxShadow: index === activeStepIndex ? 
                             '0 8px 25px rgba(25, 118, 210, 0.15)' : 
                             '0 4px 15px rgba(0, 0, 0, 0.3)'
                         },
                       '&:before': index === activeStepIndex ? {
                         content: '""',
                         position: 'absolute',
                         top: 0,
                         left: 0,
                         width: 4,
                         height: '100%',
                         backgroundColor: '#1976d2',
                         borderRadius: '0 2px 2px 0'
                       } : {}
                     }}
                    onClick={() => jumpToStep(index)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      {/* Step Number */}
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                                                     backgroundColor: index === activeStepIndex ? 
                             'rgba(25, 118, 210, 0.8)' : 
                             'rgba(255, 255, 255, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          mt: 0.5,
                                                     boxShadow: index === activeStepIndex ? 
                             '0 4px 12px rgba(25, 118, 210, 0.4)' : 
                             '0 2px 6px rgba(0, 0, 0, 0.2)',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        <Typography sx={{ 
                          color: 'white', 
                          fontSize: '0.8rem', 
                          fontWeight: 700 
                        }}>
                          {index + 1}
                        </Typography>
                      </Box>

                      {/* Step Content */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Chip
                            label={step.videoTimestampFormatted}
                            size="small"
                            sx={{
                                                           backgroundColor: index === activeStepIndex ? 
                               'rgba(25, 118, 210, 0.25)' : 
                               'rgba(25, 118, 210, 0.15)',
                             color: index === activeStepIndex ? '#1976d2' : '#1976d2',
                              fontSize: '0.75rem',
                              height: 22,
                              fontWeight: 600,
                              fontFamily: 'monospace'
                            }}
                          />
                          {index === activeStepIndex && (
                            <Fade in timeout={300}>
                              <Chip
                                label="PLAYING"
                                size="small"
                                sx={{
                                                                   backgroundColor: 'rgba(25, 118, 210, 0.25)',
                                 color: '#1976d2',
                                  fontSize: '0.65rem',
                                  height: 20,
                                  fontWeight: 700,
                                  animation: 'pulse 2s infinite'
                                }}
                              />
                            </Fade>
                          )}
                        </Box>
                        <Typography
                          sx={{
                                                         color: index === activeStepIndex ? '#e3f2fd' : '#ddd',
                            fontSize: '0.875rem',
                            lineHeight: 1.5,
                            fontWeight: index === activeStepIndex ? 500 : 400,
                            transition: 'color 0.3s ease'
                          }}
                        >
                          {step.description}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Box>
            </Box>
          </Paper>
        )}
      </Box>
    </Paper>
  );
};

const PlaywrightTestRun = ({ testRunData, loading, error }) => {
  const [activeTab, setActiveTab] = useState(1);
  const { isOpen, isHovered } = useSidebar();
  const navbarWidth = isOpen || isHovered ? 240 : 64;
  const params = useParams();
  const runId = params.runId;
  
  // Screenshot modal state
  const [selectedScreenshot, setSelectedScreenshot] = useState(null);
  const [selectedScreenshotIndex, setSelectedScreenshotIndex] = useState(null);

  // If we're using real data from params, we would fetch it here
  const [traceError, setTraceError] = useState(null);
  const [checkingTrace, setCheckingTrace] = useState(false);
  const [localTraceFile, setLocalTraceFile] = useState(null);
  const [traceBlob, setTraceBlob] = useState(null);
  const [downloadingTrace, setDownloadingTrace] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const iframeRef = useRef(null);
  
  // Flaky marking state
  const [isMarkingFlaky, setIsMarkingFlaky] = useState(false);
  const [markFlakySuccess, setMarkFlakySuccess] = useState(false);
  const [markFlakyError, setMarkFlakyError] = useState(null);
  
  // Revision request state
  const [revisionPopoverAnchor, setRevisionPopoverAnchor] = useState(null);
  const [revisionFeedback, setRevisionFeedback] = useState('');
  
  // In progress check state
  const [isCheckingInProgress, setIsCheckingInProgress] = useState(false);
  const [isInProgress, setIsInProgress] = useState(false);
  
  // Extract testResult from testRunData
  const testResult = testRunData?.result_dict;
  
  // Format duration from milliseconds to seconds
  const formattedDuration = testResult ? 
    (testResult.duration / 1000).toFixed(2) + 's' : '';
  
  // Extract filename from full path
  const fileName = testResult ? 
    testResult.file.split('/').pop() : '';
  
  // Check if there's an ongoing task for this test case
  const checkInProgress = useCallback(async () => {
    if (!testRunData?.test_case_id) return;
    
    try {
      setIsCheckingInProgress(true);
      const session = await supabase.auth.getSession();
      if (!session?.data?.session?.access_token) {
        throw new Error("Not authenticated");
      }
      
      const authToken = session.data.session.access_token;
      const headers = {
        Authorization: `Bearer ${authToken}`,
        'Content-Type': 'application/json'
      };

      const response = await axios.post(
        `${API_URL}/test/check_in_progress`,
        { 
          test_case_id: testRunData.test_case_id
        },
        { headers }
      );

      if (response.status === 200) {
        setIsInProgress(response.data.in_progress);
      } else {
        throw new Error('Failed to check in progress status');
      }
    } catch (error) {
      console.error('Error checking in progress status:', error);
      // Don't set error state here as it's not critical for the UI
    } finally {
      setIsCheckingInProgress(false);
    }
  }, [testRunData?.test_case_id]);
  
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (newValue === 2 && testResult?.artifacts?.trace?.url) {
      // Always open the trace in a new tab because Playwright trace viewer
      // requires cross-origin isolation and will not load correctly inside an iframe.
      const remoteUrl = testResult.artifacts.trace.url;
      const viewerUrl = `https://trace.playwright.dev/index.html?trace=${encodeURIComponent(remoteUrl)}`;
      window.open(viewerUrl, '_blank', 'noopener,noreferrer');
    }
  };
  
  // Function to download and extract trace file
  const downloadAndExtractTrace = async (url) => {
    try {
      setDownloadingTrace(true);
      setTraceError(null);

      // Download the zip file
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const zipBlob = await response.blob();
      
      // Load and parse the zip file
      const zip = new JSZip();
      const contents = await zip.loadAsync(zipBlob);
      
      // Find the trace file in the zip (usually with .trace extension)
      let traceFile = null;
      for (const [filename, file] of Object.entries(contents.files)) {
        if (filename.endsWith('.trace')) {
          traceFile = file;
          break;
        }
      }
      
      if (!traceFile) {
        throw new Error('No trace file found in the zip archive');
      }
      
      // Extract the trace file
      const traceContent = await traceFile.async('blob');
      setTraceBlob(traceContent);
      
    } catch (err) {
      console.error('Error processing trace:', err);
      setTraceError(`Error processing trace: ${err.message}`);
    } finally {
      setDownloadingTrace(false);
    }
  };

  // Check trace URL when component loads or tab changes to trace
  useEffect(() => {
    if (activeTab === 2 && testResult?.artifacts?.trace?.url && !traceBlob) {
      downloadAndExtractTrace(testResult.artifacts.trace.url);
    }
  }, [activeTab, testResult?.artifacts?.trace?.url]);

  // Function to send trace to iframe
  const sendTraceToIframe = useCallback(() => {
    if (!iframeRef.current || !traceBlob) return;

    // Create a FileList-like object
    const file = new File([traceBlob], 'trace.trace', { type: 'application/octet-stream' });
    const transfer = {
      files: [file]
    };

    // Send the trace file to the iframe
    iframeRef.current.contentWindow.postMessage({
      type: 'drop',
      data: transfer
    }, '*');
  }, [traceBlob]);

  // Handle iframe load
  useEffect(() => {
    if (iframeLoaded && traceBlob) {
      // Small delay to ensure iframe is fully initialized
      const timer = setTimeout(() => {
        sendTraceToIframe();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [iframeLoaded, traceBlob, sendTraceToIframe]);

  // Create the trace viewer URL
  const traceViewerUrl = 'https://trace.playwright.dev/';
      
  // Check if the trace URL is accessible
  const checkTraceUrl = async () => {
    if (!testResult?.artifacts?.trace?.url) {
      setTraceError("No trace URL available");
      return;
    }

    try {
      setCheckingTrace(true);
      setTraceError(null);
      
      // First try a HEAD request
      try {
        const response = await fetch(testResult.artifacts.trace.url, { 
          method: 'HEAD',
          mode: 'cors'
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
      } catch (headError) {
        // If HEAD fails, try GET with range to minimize data transfer
        const response = await fetch(testResult.artifacts.trace.url, {
          headers: {
            'Range': 'bytes=0-0'
          },
          mode: 'cors'
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
      }
      
      // If we get here, the URL is accessible
      setTraceError(null);
    } catch (err) {
      console.error('Trace access error:', err);
      setTraceError(`Error accessing trace: ${err.message}. Make sure CORS is enabled on the CDN and the file exists.`);
    } finally {
      setCheckingTrace(false);
    }
  };

  // Download and use trace file locally
  const downloadAndUseTraceLocally = async () => {
    try {
      setCheckingTrace(true);
      const response = await fetch(testResult.artifacts.trace.url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const blob = await response.blob();
      setLocalTraceFile(blob);
      setTraceError(null);
    } catch (err) {
      console.error('Local trace download error:', err);
      setTraceError(`Error downloading trace: ${err.message}`);
    } finally {
      setCheckingTrace(false);
    }
  };
  
  // Check trace URL when component loads or tab changes to trace
  useEffect(() => {
    if (activeTab === 2 && testResult?.artifacts?.trace?.url) {
      checkTraceUrl();
    }
  }, [activeTab, testResult?.artifacts?.trace?.url]);

  const traceBlobUrl = useMemo(() => (traceBlob ? URL.createObjectURL(traceBlob) : null), [traceBlob]);

  // Check in progress status when component loads
  useEffect(() => {
    if (testRunData?.test_case_id) {
      checkInProgress();
    }
  }, [testRunData?.test_case_id, checkInProgress]);

  // Handle screenshot navigation
  const handlePrevScreenshot = () => {
    if (selectedScreenshotIndex > 0) {
      setSelectedScreenshotIndex(selectedScreenshotIndex - 1);
      setSelectedScreenshot(testResult.artifacts.screenshots[selectedScreenshotIndex - 1]);
    }
  };

  const handleNextScreenshot = () => {
    if (selectedScreenshotIndex < testResult.artifacts.screenshots.length - 1) {
      setSelectedScreenshotIndex(selectedScreenshotIndex + 1);
      setSelectedScreenshot(testResult.artifacts.screenshots[selectedScreenshotIndex + 1]);
    }
  };

  // Handle opening revision request popover
  const handleRevisionRequest = (event) => {
    setRevisionPopoverAnchor(event.currentTarget);
    setRevisionFeedback('');
    setMarkFlakyError(null);
    setMarkFlakySuccess(false);
  };

  // Handle closing revision request popover
  const handleCloseRevisionPopover = () => {
    setRevisionPopoverAnchor(null);
    setRevisionFeedback('');
  };

  // Handle sending revision request
  const handleSendRevisionRequest = async () => {
    if (!revisionFeedback.trim()) {
      setMarkFlakyError('Please provide feedback before sending');
      return;
    }

    setIsMarkingFlaky(true);
    setMarkFlakyError(null);
    setMarkFlakySuccess(false);
    
    try {
      const session = await supabase.auth.getSession();
      if (!session?.data?.session?.access_token) {
        throw new Error("Not authenticated");
      }
      
      // Check if we have test_case_id in the test run data
      console.log('testRunData', testRunData);
      const testCaseId = testRunData?.test_case_id;
      if (!testCaseId) {
        throw new Error("Test case ID not found in test run data");
      }
      
      const authToken = session.data.session.access_token;
      const headers = {
        Authorization: `Bearer ${authToken}`,
        'Content-Type': 'application/json'
      };

      const response = await axios.post(
        `${API_URL}/test/mark_flaky`,
        { 
          test_case_id: testCaseId,
          feedback: revisionFeedback.trim()
        },
        { headers }
      );

      if (response.status === 200) {
        setMarkFlakySuccess(true);
        setRevisionPopoverAnchor(null);
        setRevisionFeedback('');
        // Reset success message after 5 seconds (longer since this process takes time)
        setTimeout(() => {
          setMarkFlakySuccess(false);
        }, 5000);
      } else {
        throw new Error('Failed to send revision request');
      }
    } catch (error) {
      console.error('Error sending revision request:', error);
      setMarkFlakyError(error.message || 'Failed to send revision request');
    } finally {
      setIsMarkingFlaky(false);
    }
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedScreenshot) return;
      
      if (e.key === 'ArrowLeft') {
        handlePrevScreenshot();
      } else if (e.key === 'ArrowRight') {
        handleNextScreenshot();
      } else if (e.key === 'Escape') {
        setSelectedScreenshot(null);
        setSelectedScreenshotIndex(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedScreenshot, selectedScreenshotIndex]);

  if (loading) {
    return (
      <>
        <NavBar />
        <Box
          component="main" 
          sx={{
            minHeight: '100vh',
            marginLeft: `${navbarWidth}px`,
            transition: 'margin-left 0.2s',
            width: `calc(100% - ${navbarWidth}px)`,
            p: 3,
            backgroundColor: '#121212'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
            <CircularProgress />
          </Box>
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <NavBar />
        <Box
          component="main" 
          sx={{
            minHeight: '100vh',
            marginLeft: `${navbarWidth}px`,
            transition: 'margin-left 0.2s',
            width: `calc(100% - ${navbarWidth}px)`,
            p: 3,
            backgroundColor: '#121212'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh', flexDirection: 'column' }}>
            <Typography color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
            <Typography variant="body2" sx={{ color: '#888' }}>
              Please check the test run ID or return to the test list.
            </Typography>
          </Box>
        </Box>
      </>
    );
  }

  if (!testResult) {
    return (
      <>
        <NavBar />
        <Box
          component="main" 
          sx={{
            minHeight: '100vh',
            marginLeft: `${navbarWidth}px`,
            transition: 'margin-left 0.2s',
            width: `calc(100% - ${navbarWidth}px)`,
            p: 3,
            backgroundColor: '#121212'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
            <Typography variant="body1" sx={{ color: '#888' }}>
              No test result data available.
            </Typography>
          </Box>
        </Box>
      </>
    );
  }

  return (
    <>
      <NavBar />
      <Box
        component="main" 
        sx={{
          minHeight: '100vh',
          marginLeft: `${navbarWidth}px`,
          transition: 'margin-left 0.2s',
          width: `calc(100% - ${navbarWidth}px)`,
          p: 3,
          backgroundColor: '#121212',
          boxSizing: 'border-box'
        }}
      >
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'flex-start',
            mb: 3
          }}
        >
          <Box>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600, 
                color: '#fff',
                mb: 1
              }}
            >
              Test Result
            </Typography>
            <Typography sx={{ color: '#888', fontSize: '0.875rem' }}>
              {runId && `Run ID: ${runId} • `}
              {testResult.created_at && formatDistanceToNow(new Date(testResult.created_at)) + ' ago'}
            </Typography>
          </Box>
          
          {/* Ask for Revision Button */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
            <Tooltip 
              title={isInProgress ? "There is an ongoing task for this test case. Please wait for it to complete." : ""}
              placement="top"
              arrow
            >
              <span>
                <Button
                  variant="contained"
                  onClick={handleRevisionRequest}
                  disabled={isMarkingFlaky || isInProgress || isCheckingInProgress}
                  startIcon={
                    isMarkingFlaky ? <CircularProgress size={16} /> : 
                    isCheckingInProgress ? <CircularProgress size={16} /> :
                    <MessageCircle size={16} />
                  }
                  sx={{
                    backgroundColor: isInProgress ? '#555' : '#ff9800',
                    color: '#fff',
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                    boxShadow: isInProgress ? 'none' : '0 4px 12px rgba(255, 152, 0, 0.3)',
                    '&:hover': {
                      backgroundColor: isInProgress ? '#555' : '#f57c00',
                      boxShadow: isInProgress ? 'none' : '0 6px 16px rgba(255, 152, 0, 0.4)',
                      transform: isInProgress ? 'none' : 'translateY(-1px)',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: '#555',
                      color: '#999',
                      boxShadow: 'none',
                    }
                  }}
                >
                  {isMarkingFlaky ? 'Processing...' : 
                   isInProgress ? 'Work In Progress...' :
                   isCheckingInProgress ? 'Checking...' :
                   'Revise Test Case'}
                </Button>
              </span>
            </Tooltip>
            
            {/* Success/Error Messages */}
            {markFlakySuccess && (
              <Typography sx={{ color: '#4caf50', fontSize: '0.75rem' }}>
                Revision request sent successfully
              </Typography>
            )}
            {markFlakyError && (
              <Typography sx={{ color: '#f44336', fontSize: '0.75rem' }}>
                {markFlakyError}
              </Typography>
            )}
          </Box>
          
          {/* Revision Request Popover */}
          <Popover
            open={Boolean(revisionPopoverAnchor)}
            anchorEl={revisionPopoverAnchor}
            onClose={handleCloseRevisionPopover}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            PaperProps={{
              sx: {
                backgroundColor: '#1A1A1A',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 1.5,
                minWidth: 350,
                maxWidth: 400,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              }
            }}
          >
            <Box sx={{ p: 3 }}>
              <Typography sx={{ color: '#fff', fontWeight: 600, mb: 2, fontSize: '1rem' }}>
                Request Test Revision
              </Typography>
              <Typography sx={{ color: '#aaa', fontSize: '0.875rem', mb: 2, lineHeight: 1.5 }}>
                Provide feedback about what needs to be revised in this test:
              </Typography>
              <TextField
                multiline
                rows={4}
                fullWidth
                placeholder="Please describe what needs to be fixed or improved in this test..."
                value={revisionFeedback}
                onChange={(e) => setRevisionFeedback(e.target.value)}
                variant="outlined"
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#242424',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.1)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1976d2',
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: '#fff',
                    fontSize: '0.875rem',
                    '&::placeholder': {
                      color: '#666',
                      opacity: 1,
                    },
                  },
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button
                  onClick={handleCloseRevisionPopover}
                  sx={{
                    color: '#aaa',
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    }
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSendRevisionRequest}
                  disabled={isMarkingFlaky || !revisionFeedback.trim()}
                  startIcon={isMarkingFlaky ? <CircularProgress size={16} /> : null}
                  sx={{
                    backgroundColor: '#1976d2',
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    px: 2,
                    '&:hover': {
                      backgroundColor: '#1565c0',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: '#333',
                      color: '#666',
                    }
                  }}
                >
                  {isMarkingFlaky ? 'Sending...' : 'Send'}
                </Button>
              </Box>
            </Box>
          </Popover>
        </Box>
        
        {/* Test Info Card */}
        <Paper
          elevation={0}
          sx={{
            backgroundColor: '#1A1A1A',
            borderRadius: 1.5,
            border: '1px solid rgba(255, 255, 255, 0.05)',
            mb: 3,
            overflow: 'hidden',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
          }}
        >
          <Box 
            sx={{ 
              p: 3,
              borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Tooltip title="Test Status">
                  {testResult.status === 'passed' ? (
                    <CheckCircle size={20} style={{ color: '#4caf50', marginRight: '16px' }} />
                  ) : testResult.status === 'skipped' ? (
                    <Layers size={20} style={{ color: '#ff9800', marginRight: '16px' }} />
                  ) : (
                    <XCircle size={20} style={{ color: '#f44336', marginRight: '16px' }} />
                  )}
                </Tooltip>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    color: '#fff',
                    fontWeight: 500,
                    fontSize: '1.25rem'
                  }}
                >
                  {testResult.title}
                </Typography>
              </Box>
              <Chip 
                label={testResult.status.toUpperCase()}
                sx={{ 
                  backgroundColor: testResult.status === 'passed' ? 'rgba(76, 175, 80, 0.15)' : 
                                 testResult.status === 'skipped' ? 'rgba(255, 152, 0, 0.15)' :
                                 'rgba(244, 67, 54, 0.15)',
                  color: testResult.status === 'passed' ? '#4caf50' : 
                         testResult.status === 'skipped' ? '#ff9800' :
                         '#f44336',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  height: 24,
                  borderRadius: '12px'
                }}
              />
            </Box>
            
            {/* Test details */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mt: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', minWidth: '180px' }}>
                <Layers size={16} style={{ color: '#1976d2', marginRight: '8px' }} />
                <Typography sx={{ color: '#999', fontSize: '0.875rem', mr: 1 }}>Project:</Typography>
                <Typography sx={{ color: '#fff', fontSize: '0.875rem', fontWeight: 500 }}>
                  {testResult.projectName}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', minWidth: '150px' }}>
                <Clock size={16} style={{ color: '#1976d2', marginRight: '8px' }} />
                <Typography sx={{ color: '#999', fontSize: '0.875rem', mr: 1 }}>Duration:</Typography>
                <Typography sx={{ color: '#fff', fontSize: '0.875rem', fontWeight: 500 }}>
                  {formattedDuration}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, mt: { xs: 1, md: 0 } }}>
                <FileSpreadsheet size={16} style={{ color: '#1976d2', marginRight: '8px', flexShrink: 0 }} />
                <Typography sx={{ color: '#999', fontSize: '0.875rem', mr: 1, flexShrink: 0 }}>File:</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                  <Typography 
                    sx={{ 
                      color: '#fff', 
                      fontSize: '0.875rem',
                      fontFamily: 'monospace',
                      bgcolor: 'rgba(255, 255, 255, 0.05)', 
                      px: 1, 
                      py: 0.5, 
                      borderRadius: 1,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '250px'
                    }}
                  >
                    {fileName}
                  </Typography>
                  <Tooltip title={testResult.file}>
                    <Typography 
                      sx={{ 
                        color: '#666', 
                        fontSize: '0.75rem', 
                        ml: 1,
                        whiteSpace: 'nowrap',
                        display: { xs: 'none', md: 'block' }
                      }}
                    >
                      {testResult.file}
                    </Typography>
                  </Tooltip>
                </Box>
              </Box>
            </Box>
          </Box>
          
          {/* Annotations Section */}
          {testResult.annotations && testResult.annotations.length > 0 && (
            <Accordion
              defaultExpanded={true}
              elevation={0}
              sx={{
                backgroundColor: '#1A1A1A',
                borderRadius: 1.5,
                border: '1px solid rgba(255, 255, 255, 0.05)',
                mb: 3,
                overflow: 'hidden',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                '&:before': {
                  display: 'none',
                },
                '& .MuiAccordionSummary-root': {
                  backgroundColor: '#1A1A1A',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                  minHeight: 56,
                  px: 3,
                  py: 2,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.02)',
                  },
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'center',
                    my: 1,
                  },
                  '& .MuiAccordionSummary-expandIconWrapper': {
                    color: '#1976d2',
                  },
                },
                '& .MuiAccordionDetails-root': {
                  p: 0,
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ChevronDown size={20} />}
                aria-controls="annotations-content"
                id="annotations-header"
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Info size={20} style={{ color: '#1976d2' }} />
                  <Typography sx={{ color: '#fff', fontWeight: 500, fontSize: '1rem' }}>
                    Test Annotations
                  </Typography>
                  <Chip
                    label={testResult.annotations.length}
                    size="small"
                    sx={{
                      backgroundColor: 'rgba(25, 118, 210, 0.15)',
                      color: '#1976d2',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      height: 20,
                      borderRadius: '10px',
                      ml: 1
                    }}
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {testResult.annotations.map((annotation, index) => (
                      <Paper
                        key={index}
                        elevation={0}
                        sx={{
                          backgroundColor: '#242424',
                          borderRadius: 1.5,
                          border: '1px solid rgba(255, 255, 255, 0.05)',
                          p: 3,
                          position: 'relative',
                          overflow: 'hidden',
                          '&:before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: 4,
                            height: '100%',
                            backgroundColor: annotation.type === 'Agent Early Pass' ? '#4caf50' : 
                                           annotation.type.toLowerCase().includes('error') ? '#f44336' : 
                                           annotation.type.toLowerCase().includes('warning') ? '#ff9800' : 
                                           '#1976d2',
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                          <Box sx={{ mt: 0.5 }}>
                            {annotation.type === 'Agent Early Pass' ? (
                              <CheckCircle2 size={20} style={{ color: '#4caf50' }} />
                            ) : annotation.type.toLowerCase().includes('error') ? (
                              <XCircle size={20} style={{ color: '#f44336' }} />
                            ) : annotation.type.toLowerCase().includes('warning') ? (
                              <AlertCircle size={20} style={{ color: '#ff9800' }} />
                            ) : (
                              <Info size={20} style={{ color: '#1976d2' }} />
                            )}
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ mb: 1 }}>
                              <Typography
                                sx={{
                                  color: '#fff',
                                  fontWeight: 600,
                                  fontSize: '0.875rem',
                                  letterSpacing: '0.02em'
                                }}
                              >
                                {annotation.type}
                              </Typography>
                            </Box>
                            <Typography
                              sx={{
                                color: '#ccc',
                                fontSize: '0.875rem',
                                lineHeight: 1.6,
                                whiteSpace: 'pre-wrap'
                              }}
                            >
                              {annotation.description}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          )}
          
          {/* Tabs Navigation */}
          <Paper
            elevation={0}
            sx={{
              backgroundColor: '#1A1A1A',
              borderRadius: 1.5,
              border: '1px solid rgba(255, 255, 255, 0.05)',
              mb: 3,
              overflow: 'hidden',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
            }}
          >
            {/* Error Message Section - Moved here */}
            {testResult.status === 'failed' && testResult.error && (
              <Box>
                <Box sx={{ 
                  p: 2, 
                  borderBottom: '1px solid rgba(244, 67, 54, 0.2)',
                  backgroundColor: 'rgba(244, 67, 54, 0.1)',
                }}>
                  <Typography 
                    sx={{ 
                      color: '#f44336',
                      fontWeight: 500,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <XCircle size={18} />
                    Error Details
                  </Typography>
                </Box>
                <Box 
                  sx={{ 
                    p: 3,
                    backgroundColor: '#1A1A1A',
                    fontFamily: '"Roboto Mono", monospace',
                    fontSize: '0.875rem',
                    whiteSpace: 'pre-wrap',
                    overflowX: 'auto',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
                  }}
                >
                  <Typography
                    component="pre"
                    sx={{
                      color: '#ff867c',
                      m: 0,
                      fontFamily: 'inherit',
                      fontSize: 'inherit',
                      lineHeight: 1.5
                    }}
                  >
                    {cleanAnsiCodes(testResult.error)}
                  </Typography>
                </Box>
              </Box>
            )}
            
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{
                borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                '& .MuiTabs-indicator': {
                  backgroundColor: '#1976d2',
                },
                '& .MuiTab-root': {
                  color: '#999',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  minHeight: '48px',
                  '&.Mui-selected': {
                    color: '#1976d2',
                  }
                }
              }}
            >
              <Tab 
                icon={<Eye size={18} />} 
                iconPosition="start" 
                label="Screenshots"
                sx={{ textTransform: 'none', py: 1.5, minWidth: '130px' }}
              />
              <Tab 
                icon={<Video size={18} />} 
                iconPosition="start" 
                label="Video"
                sx={{ textTransform: 'none', py: 1.5, minWidth: '110px' }}
              />
              <Tab 
                icon={<FileSpreadsheet size={18} />} 
                iconPosition="start" 
                label="Trace"
                sx={{ textTransform: 'none', py: 1.5, minWidth: '110px' }}
              />
            </Tabs>

            {/* Tab Panels */}
            <Box sx={{ p: 3 }}>
              {/* Screenshots Tab */}
              {activeTab === 0 && (
                <Fade in={activeTab === 0} timeout={300}>
                  <div>
                    {testResult.artifacts?.screenshots?.length > 0 ? (
                      <Box>
                        <Box 
                          sx={{ 
                            display: 'grid',
                            gap: 3,
                            gridTemplateColumns: {
                              xs: 'repeat(1, 1fr)',
                              sm: 'repeat(2, 1fr)',
                              md: 'repeat(3, 1fr)',
                              lg: 'repeat(4, 1fr)'
                            }
                          }}
                        >
                          {testResult.artifacts.screenshots.map((screenshot, index) => (
                            <Paper
                              key={index}
                              elevation={0}
                              sx={{
                                backgroundColor: '#242424',
                                borderRadius: 1.5,
                                border: '1px solid rgba(255, 255, 255, 0.05)',
                                overflow: 'hidden',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column'
                              }}
                            >
                              <Box sx={{ position: 'relative', width: '100%', flexGrow: 1 }}>
                                <Box
                                  component="img"
                                  src={screenshot.url}
                                  alt={`Test screenshot ${index + 1}`}
                                  onClick={() => {
                                    setSelectedScreenshot(screenshot);
                                    setSelectedScreenshotIndex(index);
                                  }}
                                  sx={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    display: 'block',
                                    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                                    cursor: 'pointer',
                                    transition: 'transform 0.2s ease-in-out',
                                    '&:hover': {
                                      transform: 'scale(1.02)',
                                    }
                                  }}
                                />
                              </Box>
                              <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography sx={{ color: '#ccc', fontSize: '0.875rem' }}>
                                  Screenshot {index + 1}
                                </Typography>
                                <Tooltip title="View Full Size">
                                  <IconButton 
                                    href={screenshot.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    size="small"
                                    sx={{ color: '#1976d2' }}
                                  >
                                    <ExternalLink size={18} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Paper>
                          ))}
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 6 }}>
                        <Typography sx={{ color: '#666', fontSize: '0.875rem' }}>
                          No screenshots available for this test run.
                        </Typography>
                      </Box>
                    )}
                  </div>
                </Fade>
              )}

              {/* Video Tab */}
              {activeTab === 1 && (
                <Fade in={activeTab === 1} timeout={300}>
                                      <div>
                      {testResult.artifacts?.video?.url ? (
                        // Use custom video player if steps are available, otherwise use default player
                        testResult.steps?.steps?.length > 0 ? (
                          <CustomVideoPlayer 
                            videoUrl={testResult.artifacts.video.url} 
                            steps={testResult.steps} 
                            posterUrl={testResult.artifacts.screenshots?.[0]?.url} 
                          />
                        ) : (
                          <Paper
                            elevation={0}
                            sx={{
                              backgroundColor: '#242424',
                              borderRadius: 1.5,
                              border: '1px solid rgba(255, 255, 255, 0.05)',
                              overflow: 'hidden'
                            }}
                          >
                            <video
                              controls
                              width="100%"
                              poster={testResult.artifacts?.screenshots?.[0]?.url}
                              style={{
                                backgroundColor: '#1A1A1A',
                                borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
                              }}
                            >
                              <source src={testResult.artifacts.video.url} type="video/webm" />
                              Your browser does not support the video tag.
                            </video>
                            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography sx={{ color: '#ccc', fontSize: '0.875rem' }}>
                                Test Execution Recording
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Tooltip title="Download Video">
                                  <IconButton 
                                    href={testResult.artifacts.video.url}
                                    target="_blank"
                                    rel="noopener noreferrer" 
                                    size="small"
                                    sx={{ color: '#1976d2' }}
                                  >
                                    <ExternalLink size={18} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                          </Paper>
                        )
                      ) : (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                          <Typography sx={{ color: '#666', fontSize: '0.875rem' }}>
                            No video recording available for this test run.
                          </Typography>
                        </Box>
                      )}
                    </div>
                </Fade>
              )}

              {/* Trace Tab */}
              {activeTab === 2 && (
                <Fade in={activeTab === 2} timeout={300}>
                  <div>
                    {testResult.artifacts?.trace?.url ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <Paper
                          elevation={0}
                          sx={{
                            backgroundColor: '#242424',
                            borderRadius: 1.5,
                            border: '1px solid rgba(255, 255, 255, 0.05)',
                            overflow: 'hidden'
                          }}
                        >
                          <Box sx={{ p: 2, borderBottom: '1px solid rgba(255, 255, 255, 0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography sx={{ color: '#fff', fontWeight: 500 }}>
                              Trace Viewer
                            </Typography>
                          </Box>
                          
                          {traceError && (
                            <Box sx={{ p: 2 }}>
                              <Alert 
                                severity="error" 
                                sx={{ 
                                  backgroundColor: 'rgba(244, 67, 54, 0.1)', 
                                  color: '#f44336',
                                  '& .MuiAlert-icon': {
                                    color: '#f44336'
                                  }
                                }}
                              >
                                <Typography variant="body2">
                                  {traceError}
                                </Typography>
                              </Alert>
                            </Box>
                          )}
                          
                          <Box sx={{ position: 'relative', width: '100%', height: 0, paddingTop: '56.25%' }}>
                            {!traceError && testResult?.artifacts?.trace?.url ? (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  width: '100%',
                                  height: '100%',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  bgcolor: '#1A1A1A',
                                  p: 3,
                                  textAlign: 'center',
                                  gap: 3
                                }}
                              >
                                <Typography sx={{ color: '#aaa' }}>
                                  For the best experience the trace viewer is opened in a separate tab. If it did not open automatically you can use the button below.
                                </Typography>
                                <Button
                                  variant="contained"
                                  color="primary"
                                  href={`https://trace.playwright.dev/index.html?trace=${encodeURIComponent(testResult.artifacts.trace.url)}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  startIcon={<ExternalLink size={16} />}
                                >
                                  Open Trace Viewer
                                </Button>
                              </Box>
                            ) : (
                              <Box sx={{ textAlign: 'center', py: 6 }}>
                                <Typography sx={{ color: '#666', fontSize: '0.875rem' }}>
                                  No trace data available for this test run.
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Paper>
                      </Box>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 6 }}>
                        <Typography sx={{ color: '#666', fontSize: '0.875rem' }}>
                          No trace data available for this test run.
                        </Typography>
                      </Box>
                    )}
                  </div>
                </Fade>
              )}
            </Box>
          </Paper>
        </Paper>

        {/* Screenshots Modal */}
        <Modal
          open={!!selectedScreenshot}
          onClose={() => {
            setSelectedScreenshot(null);
            setSelectedScreenshotIndex(null);
          }}
          closeAfterTransition
          BackdropComponent={Backdrop}
          BackdropProps={{
            timeout: 500,
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '& .MuiBackdrop-root': {
              backgroundColor: 'rgba(0, 0, 0, 0.85)',
            },
          }}
        >
          <Fade in={!!selectedScreenshot}>
            <Box
              sx={{
                position: 'relative',
                maxWidth: '90vw',
                maxHeight: '90vh',
                outline: 'none',
              }}
            >
              {/* Close button */}
              <IconButton
                onClick={() => {
                  setSelectedScreenshot(null);
                  setSelectedScreenshotIndex(null);
                }}
                sx={{
                  position: 'absolute',
                  top: -40,
                  right: 0,
                  color: 'white',
                  bgcolor: 'rgba(0, 0, 0, 0.5)',
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.7)',
                  },
                }}
              >
                <X size={20} />
              </IconButton>

              {/* Navigation buttons */}
              {selectedScreenshotIndex > 0 && (
                <IconButton
                  onClick={handlePrevScreenshot}
                  sx={{
                    position: 'absolute',
                    left: -56,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'white',
                    bgcolor: 'rgba(0, 0, 0, 0.5)',
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.7)',
                    },
                  }}
                >
                  <ChevronLeft size={24} />
                </IconButton>
              )}
              
              {selectedScreenshotIndex < (testResult?.artifacts?.screenshots?.length - 1) && (
                <IconButton
                  onClick={handleNextScreenshot}
                  sx={{
                    position: 'absolute',
                    right: -56,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'white',
                    bgcolor: 'rgba(0, 0, 0, 0.5)',
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.7)',
                    },
                  }}
                >
                  <ChevronRight size={24} />
                </IconButton>
              )}

              {/* Screenshot */}
              <Box
                component="img"
                src={selectedScreenshot?.url}
                alt="Screenshot"
                sx={{
                  maxWidth: '100%',
                  maxHeight: '90vh',
                  objectFit: 'contain',
                  borderRadius: 1,
                }}
              />

              {/* Screenshot counter */}
              <Typography
                sx={{
                  position: 'absolute',
                  bottom: -35,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  color: 'white',
                  fontSize: '0.875rem',
                }}
              >
                {selectedScreenshotIndex + 1} of {testResult?.artifacts?.screenshots?.length}
              </Typography>
            </Box>
          </Fade>
        </Modal>
      </Box>
    </>
  );
};

const PlaywrightTestRunPage = () => {
  const params = useParams();
  const [testRunData, setTestRunData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTestRunDetails = async () => {
      try {
        let authToken = null;
        try {
          const sessionRes = await supabase.auth.getSession();
          authToken = sessionRes?.data?.session?.access_token || null;
        } catch (_) {
          authToken = null;
        }
        const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};

        const response = await axios.get(`${API_URL}/test/playwright_test_run_details?test_run_id=${params.runId}`, {
          headers: headers,
        });

        if (response.data) {
          setTestRunData(response.data);
        } else {
          throw new Error('Invalid response format');
        }
      } catch (err) {
        console.error('Error fetching test run details:', err);
        setError(err.message || 'Failed to fetch test run details');
      } finally {
        setLoading(false);
      }
    };

    if (params.runId) {
      fetchTestRunDetails();
    }
  }, [params.runId]);

  return <PlaywrightTestRun testRunData={testRunData} loading={loading} error={error} />;
};

export default PlaywrightTestRunPage;
